import React, { useCallback, useEffect, useRef, useState } from "react";
import { useDropzone } from "react-dropzone";
import toast from "react-hot-toast";
import Loader from "../Loader/Loader";
import axios from "axios";
import { MdOutlineUploadFile } from "react-icons/md";

const uploadFiles = async (files) => {
  try {
    const uploadPromises = files.map(async (file) => {
      const formData = new FormData();
      formData.append("file", file);

      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/v1/s3`,
        formData,
        {
          headers: {
            "Content-type": "multipart/form-data",
            Accept: "multipart/form-data",
            Authorization:
              "Bearer " + JSON.parse(localStorage.getItem("tykoon_access")),
          },
        }
      );
      return response.data.photos[0]; // Assuming the API returns { photos: [url] }
    });

    const uploadedPaths = await Promise.all(uploadPromises);
    return uploadedPaths;
  } catch (error) {
    console.error("Error uploading images:", error);
    return [];
  }
};

const CustomFilesDropper = ({
  title,
  instructions,
  onFileDrop,
  type,
  fileTypes,
  defaultFiles = [],
  maxFiles = 5,
}) => {
  const [droppedFiles, setDroppedFiles] = useState([]);
  const [previewFiles, setPreviewFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const videoRefs = useRef([]);

  const handleVideoControl = useCallback(() => {
    videoRefs.current.forEach((video) => {
      if (video) {
        const { videoWidth, videoHeight } = video;

        video.onplay = async () => {
          if (video.requestFullscreen) {
            await video.requestFullscreen();
          } else if (video.mozRequestFullScreen) {
            await video.mozRequestFullScreen();
          } else if (video.webkitRequestFullscreen) {
            await video.webkitRequestFullscreen();
          } else if (video.msRequestFullscreen) {
            await video.msRequestFullscreen();
          }

          const isFullScreen = document.fullscreenElement === video;

          if (isFullScreen) {
            video.play();
          } else if (videoWidth <= 1280 || videoHeight <= 720) {
            video.pause();
            video.onplay = () => video.pause();
          } else {
            video.play();
          }
        };
      }
    });
  }, []);

  const handleFullScreenChange = useCallback(() => {
    videoRefs.current.forEach((video) => {
      if (video) {
        const isFullScreen = document.fullscreenElement === video;
        const { videoWidth, videoHeight } = video;

        if (!isFullScreen && (videoWidth <= 1280 || videoHeight <= 720)) {
          video.pause();
          video.onplay = () => video.pause();
        }
      }
    });
  }, []);

  useEffect(() => {
    const checkVideoSize = () => {
      handleVideoControl();
    };

    checkVideoSize();

    videoRefs.current.forEach((video) => {
      if (video) {
        video.onloadedmetadata = checkVideoSize;
      }
    });

    window.addEventListener("resize", checkVideoSize);
    document.addEventListener("fullscreenchange", handleFullScreenChange);

    return () => {
      window.removeEventListener("resize", checkVideoSize);
      document.removeEventListener("fullscreenchange", handleFullScreenChange);
    };
  }, [handleVideoControl, handleFullScreenChange]);

  useEffect(() => {
    // Initialize previewFiles with defaultFiles if no droppedFiles exist
    if (droppedFiles.length === 0 && defaultFiles.length > 0) {
      const previews = defaultFiles.map((fileURL) => ({
        fileURL,
        fileType: fileURL.split(".").pop().toLowerCase(),
        isDefault: true,
      }));
      setPreviewFiles(previews);
    }
  }, [defaultFiles, droppedFiles.length]);

  const onDrop = useCallback(
    async (acceptedFiles) => {
      // Check if adding new files exceeds maxFiles
      if (droppedFiles.length + acceptedFiles.length > maxFiles) {
        toast.error(`You can only upload a maximum of ${maxFiles} file(s).`);
        return;
      }

      const validFiles = acceptedFiles.filter((file) => {
        if (fileTypes.includes(file.type)) {
          return true;
        } else {
          toast.error(`File type ${file.type} is not supported.`);
          return false;
        }
      });

      if (validFiles.length === 0) return;
      setLoading(true);

      try {
        // Upload new files
        const uploadedFilePaths = await uploadFiles(validFiles);

        // Append new files to droppedFiles
        setDroppedFiles((prev) => [...prev, ...validFiles]);

        // Generate previews for new files
        const newPreviews = validFiles.map((file) => ({
          fileURL: URL.createObjectURL(file),
          fileType: file.type.split("/")[1],
          isDefault: false,
        }));

        // Append new previews to previewFiles
        setPreviewFiles((prev) => [...prev, ...newPreviews]);

        // Notify parent with all file paths (default + uploaded)
        const allFilePaths = [
          ...previewFiles.filter((f) => f.isDefault).map((f) => f.fileURL),
          ...uploadedFilePaths,
        ];
        onFileDrop(allFilePaths, type);
        toast.success("Files uploaded successfully!");

        handleVideoControl();
      } catch (error) {
        toast.error("File upload failed!");
        console.error("File upload error:", error);
      } finally {
        setLoading(false);
      }
    },
    [
      fileTypes,
      maxFiles,
      onFileDrop,
      type,
      handleVideoControl,
      droppedFiles,
      previewFiles,
    ]
  );

  const removeFile = (index, e) => {
    e.stopPropagation();
    const updatedDroppedFiles = droppedFiles.filter((_, i) => i !== index);
    const updatedPreviewFiles = previewFiles.filter((_, i) => i !== index);

    setDroppedFiles(updatedDroppedFiles);
    setPreviewFiles(updatedPreviewFiles);

    // Notify parent with updated file paths
    const updatedPaths = updatedPreviewFiles.map((file) => file.fileURL);
    onFileDrop(updatedPaths.length ? updatedPaths : null, type);
  };

  const { getRootProps, getInputProps } = useDropzone({ onDrop, maxFiles });

  return (
    <div
      {...getRootProps()}
      className={`flex flex-col items-center justify-center w-full min-h-32 p-6 border-2 border-dashed rounded-md cursor-pointer 
        ${
          previewFiles.length > 0 ? "border-blue-400" : "border-orange-400"
        } bg-orange-100`}
    >
      <input {...getInputProps()} />
      <div className="flex justify-center">
        {previewFiles.length === 0 && !loading && (
          <div className="flex flex-row items-center space-x-4">
            <MdOutlineUploadFile className="w-12 h-12 text-orange-500" />
            <div className="sm:text-lg text-[10px]">
              <p className="text-gray-600">{title}</p>
              <p className="text-xs text-slate-400">{instructions}</p>
            </div>
          </div>
        )}
        {loading && (
          <div className="mt-2">
            <Loader />
          </div>
        )}
        {!loading && previewFiles.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-2">
            {previewFiles.map((file, index) => (
              <div key={index} className="flex flex-col items-center">
                {[
                  "png",
                  "avif",
                  "gif",
                  "jpg",
                  "jpeg",
                  "jfif",
                  "pjpeg",
                  "pjp",
                ].includes(file.fileType) && (
                  <img
                    src={file.fileURL}
                    alt="preview"
                    className="w-16 h-16 object-cover rounded-md"
                  />
                )}
                {["mp4", "mov", "avi"].includes(file.fileType) && (
                  <video
                    ref={(el) => (videoRefs.current[index] = el)}
                    src={file.fileURL}
                    className="w-16 h-16 object-cover rounded-md"
                    controls
                  />
                )}
                {["pdf", "doc", "docx"].includes(file.fileType) && (
                  <embed
                    src={file.fileURL}
                    type="application/pdf"
                    className="w-16 h-16 object-cover rounded-md"
                  />
                )}
                <button
                  className="mt-1 px-2 py-1 bg-red-500 text-white rounded-md text-xs"
                  onClick={(e) => removeFile(index, e)}
                >
                  Remove
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default CustomFilesDropper;
